import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, Check, Archive, RefreshCw, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "reahq_admin_token";

const Admin = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [password, setPassword] = useState("");
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");

    const headers = { "X-Admin-Token": token };

    const fetchLeads = async (t = token) => {
        if (!t) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Token": t } });
            setLeads(res.data || []);
        } catch (e) {
            if (e?.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
                toast.error("Session expired. Please log in again.");
            } else {
                toast.error("Could not load leads.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchLeads(token);
        // eslint-disable-next-line
    }, [token]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!password) return;
        try {
            const res = await axios.post(`${API}/admin/login`, { password });
            const t = res.data.token;
            localStorage.setItem(TOKEN_KEY, t);
            setToken(t);
            toast.success("Welcome back.");
        } catch {
            toast.error("Invalid password.");
        }
    };

    const handleLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setLeads([]);
    };

    const updateStatus = async (id, status) => {
        try {
            await axios.patch(`${API}/leads/${id}`, { status }, { headers });
            setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
            toast.success("Updated.");
        } catch {
            toast.error("Update failed.");
        }
    };

    const removeLead = async (id) => {
        if (!window.confirm("Delete this lead permanently?")) return;
        try {
            await axios.delete(`${API}/leads/${id}`, { headers });
            setLeads((ls) => ls.filter((l) => l.id !== id));
            toast.success("Deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };

    const visible = filter === "all" ? leads : leads.filter((l) => l.status === filter);

    if (!token) {
        return (
            <div className="min-h-screen bg-brand-jet flex items-center justify-center px-6" data-testid="admin-login-view">
                <form
                    onSubmit={handleLogin}
                    className="w-full max-w-md bg-brand-onyx border border-brand-gold/20 p-10"
                    data-testid="admin-login-form"
                >
                    <div className="text-center">
                        <div className="font-cursive text-5xl text-brand-gold wordmark-glow">Admin</div>
                        <p className="mt-3 text-[0.65rem] tracking-[0.45em] uppercase text-brand-silver/60">
                            Inquiry Inbox
                        </p>
                    </div>
                    <div className="mt-10">
                        <label className="text-[0.65rem] tracking-[0.35em] uppercase text-brand-silver/60">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-3 w-full bg-transparent border-b border-white/20 focus:border-brand-gold outline-none py-3 text-brand-silver"
                            data-testid="admin-password-input"
                            autoFocus
                        />
                    </div>
                    <button
                        type="submit"
                        className="mt-10 w-full border border-brand-gold bg-brand-gold text-brand-jet py-4 text-xs tracking-[0.35em] uppercase hover:bg-transparent hover:text-brand-gold transition-all"
                        data-testid="admin-login-button"
                    >
                        Enter
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-brand-jet" data-testid="admin-dashboard">
            <header className="border-b border-white/10 px-6 lg:px-12 py-5 flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="font-cursive text-3xl text-brand-gold">Admin Inbox</div>
                    <p className="text-[0.6rem] tracking-[0.4em] uppercase text-brand-silver/50 mt-1">
                        Real Estate &amp; Administration Inc
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => fetchLeads()}
                        className="text-xs tracking-[0.3em] uppercase border border-white/15 text-brand-silver/80 px-4 py-2.5 hover:border-brand-gold hover:text-brand-gold transition"
                        data-testid="admin-refresh-button"
                    >
                        <RefreshCw size={14} className="inline mr-2" /> Refresh
                    </button>
                    <button
                        onClick={handleLogout}
                        className="text-xs tracking-[0.3em] uppercase border border-white/15 text-brand-silver/80 px-4 py-2.5 hover:border-brand-gold hover:text-brand-gold transition"
                        data-testid="admin-logout-button"
                    >
                        <LogOut size={14} className="inline mr-2" /> Sign out
                    </button>
                </div>
            </header>

            <div className="px-6 lg:px-12 py-8">
                <div className="flex gap-3 mb-8 flex-wrap">
                    {["all", "new", "contacted", "archived"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`text-[0.65rem] tracking-[0.35em] uppercase px-4 py-2 border transition ${
                                filter === f
                                    ? "border-brand-gold text-brand-gold bg-brand-gold/5"
                                    : "border-white/10 text-brand-silver/60 hover:border-brand-gold/50"
                            }`}
                            data-testid={`admin-filter-${f}`}
                        >
                            {f} {f === "all" ? `(${leads.length})` : `(${leads.filter((l) => l.status === f).length})`}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <p className="text-brand-silver/60">Loading…</p>
                ) : visible.length === 0 ? (
                    <p className="text-brand-silver/60 py-20 text-center" data-testid="admin-empty-state">
                        No leads in this view.
                    </p>
                ) : (
                    <div className="space-y-4">
                        {visible.map((l) => (
                            <article
                                key={l.id}
                                className="border border-white/10 bg-brand-onyx p-6 md:p-8 grid md:grid-cols-12 gap-6"
                                data-testid={`admin-lead-${l.id}`}
                            >
                                <div className="md:col-span-4">
                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                        <span
                                            className={`text-[0.6rem] tracking-[0.35em] uppercase px-2 py-1 border ${
                                                l.status === "new"
                                                    ? "border-brand-gold text-brand-gold"
                                                    : l.status === "contacted"
                                                    ? "border-brand-silver/40 text-brand-silver/80"
                                                    : "border-white/10 text-brand-silver/50"
                                            }`}
                                        >
                                            {l.status}
                                        </span>
                                        <span className="text-[0.6rem] tracking-widest uppercase text-brand-silver/50">
                                            {l.interest}
                                        </span>
                                    </div>
                                    <h3 className="font-serif-editorial text-2xl text-brand-champagne">{l.name}</h3>
                                    <div className="mt-3 space-y-1.5 text-sm">
                                        <a href={`mailto:${l.email}`} className="text-brand-silver/80 hover:text-brand-gold flex items-center gap-2">
                                            <Mail size={13} /> {l.email}
                                        </a>
                                        {l.phone && (
                                            <a href={`tel:${l.phone}`} className="text-brand-silver/80 hover:text-brand-gold flex items-center gap-2">
                                                <Phone size={13} /> {l.phone}
                                            </a>
                                        )}
                                        {l.budget && (
                                            <div className="text-brand-silver/60 text-xs">Budget: {l.budget}</div>
                                        )}
                                        <div className="text-brand-silver/50 text-xs">
                                            {new Date(l.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                                <div className="md:col-span-6">
                                    <p className="text-brand-silver/80 leading-relaxed whitespace-pre-wrap">{l.message}</p>
                                </div>
                                <div className="md:col-span-2 flex md:flex-col gap-2 md:items-end">
                                    <button
                                        onClick={() => updateStatus(l.id, "contacted")}
                                        disabled={l.status === "contacted"}
                                        className="text-[0.6rem] tracking-[0.3em] uppercase border border-brand-gold/50 text-brand-gold px-3 py-2 hover:bg-brand-gold hover:text-brand-jet transition disabled:opacity-40"
                                        data-testid={`admin-lead-contacted-${l.id}`}
                                    >
                                        <Check size={12} className="inline mr-1" /> Contacted
                                    </button>
                                    <button
                                        onClick={() => updateStatus(l.id, "archived")}
                                        disabled={l.status === "archived"}
                                        className="text-[0.6rem] tracking-[0.3em] uppercase border border-white/15 text-brand-silver/70 px-3 py-2 hover:border-brand-gold hover:text-brand-gold transition disabled:opacity-40"
                                        data-testid={`admin-lead-archive-${l.id}`}
                                    >
                                        <Archive size={12} className="inline mr-1" /> Archive
                                    </button>
                                    <button
                                        onClick={() => removeLead(l.id)}
                                        className="text-[0.6rem] tracking-[0.3em] uppercase border border-red-800/60 text-red-400 px-3 py-2 hover:bg-red-900/30 transition"
                                        data-testid={`admin-lead-delete-${l.id}`}
                                    >
                                        <Trash2 size={12} className="inline mr-1" /> Delete
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Admin;
